import { MONACO_SESSION_STORAGE_KEY } from "@nitro-ape/shared/lib/constants";
import { MonacoSDK } from "@0xmonaco/core";
import type { AuthState } from "@0xmonaco/types";
import { isServer } from "@tanstack/react-query";

/**
 * Persisted Monaco session.
 *
 * Under the session-key auth scheme the ed25519 keypair (carried on
 * {@link AuthState}) is the long-lived credential: persisting it lets us
 * restore a session across reloads without re-prompting the wallet. We store
 * the full {@link AuthState} (keypair + expiry + user) so it can be handed
 * straight back to `sdk.setAuthState`.
 */
export function getMonacoSession(): AuthState | undefined {
  if (isServer || typeof window === "undefined") return undefined;
  try {
    const raw = window.localStorage.getItem(MONACO_SESSION_STORAGE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<AuthState>;
    if (
      typeof parsed?.sessionPublicKey === "string" &&
      typeof parsed?.sessionPrivateKey === "string"
    ) {
      return parsed as AuthState;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

export function setMonacoSession(authState: AuthState | undefined): void {
  if (isServer || typeof window === "undefined") return;
  if (!authState) {
    clearMonacoSession();
    return;
  }
  try {
    window.localStorage.setItem(
      MONACO_SESSION_STORAGE_KEY,
      JSON.stringify(authState),
    );
  } catch {
    // Ignore storage write failures (e.g. private mode quota errors)
  }
}

export function clearMonacoSession(): void {
  if (isServer || typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(MONACO_SESSION_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
}

function isAuthError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    if (
      message.includes("401") ||
      message.includes("403") ||
      message.includes("unauthorized") ||
      message.includes("forbidden")
    ) {
      return true;
    }
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    (error.status === 401 || error.status === 403)
  ) {
    return true;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "statusCode" in error &&
    (error.statusCode === 401 || error.statusCode === 403)
  ) {
    return true;
  }

  return false;
}

let refreshPromise: Promise<boolean> | null = null;
let lastRefreshTime = 0;
const REFRESH_DEBOUNCE_MS = 5000;

/**
 * Extend the current Monaco session by signing a refresh request with the
 * active session key. The keypair itself does not change; we just persist the
 * updated expiry. Returns false (and clears the stored session) when the
 * session is expired/revoked so callers can fall back to a fresh login.
 */
export async function refreshMonacoToken(sdk: MonacoSDK): Promise<boolean> {
  const now = Date.now();

  if (refreshPromise && now - lastRefreshTime < REFRESH_DEBOUNCE_MS) {
    return refreshPromise;
  }

  lastRefreshTime = now;
  refreshPromise = (async () => {
    if (!getMonacoSession()) {
      return false;
    }

    try {
      const authState = await sdk.refreshAuth();
      setMonacoSession(authState);
      return true;
    } catch (error) {
      console.error("Failed to refresh Monaco session:", error);
      clearMonacoSession();
      return false;
    }
  })();

  return refreshPromise;
}

/**
 * Wrapper that handles 401/403 errors with automatic session refresh and retry.
 * Use this to wrap SDK operations that require authentication.
 *
 * @example
 * const profile = await withMonacoAuth(sdk, () => sdk.profile.getProfile());
 */
export async function withMonacoAuth<T>(
  sdk: MonacoSDK,
  operation: () => Promise<T>,
): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    if (isAuthError(error)) {
      const refreshSuccess = await refreshMonacoToken(sdk);

      if (refreshSuccess) {
        return await operation();
      }
    }

    throw error;
  }
}
